import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Checkbox } from "@/core/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateBeneficiary } from "@/features/wallet/api/use-create-beneficiary";
import {
  createBeneficiarySchema,
  type BeneficiaryFormValues,
} from "@/features/wallet/schemas/beneficiary.schema";
import { BENEFICIARY_TYPES } from "@/features/shared/api/enums";
import { WalletLookupByPhone } from "./wallet-lookup-by-phone";

/**
 * Formulaire d'ajout d'un bénéficiaire (`POST /Beneficiaries/add`) au
 * portefeuille courant — `walletId` vient du contexte, pas de la saisie.
 * TanStack Form + schéma zod mémoïsé sur la langue courante (règle projet).
 */
export function BeneficiaryFormDialog({
  open,
  onOpenChange,
  walletId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  walletId: string;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // Les clés wallet.beneficiaries.* vivent dans
  // features/wallet/i18n/beneficiaries.json en attendant leur fusion dans
  // src/locales par le coordinateur.
  const tBeneficiary = (key: string) => t(key as TFunctionType);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createBeneficiarySchema(), [i18n.language]);

  const createBeneficiary = useCreateBeneficiary();
  const pending = createBeneficiary.isPending;
  const [preview, setPreview] = useState<BeneficiaryFormValues | null>(null);

  // Radix démonte le contenu du Dialog à la fermeture : à chaque ouverture,
  // le formulaire repart de valeurs vides.
  const form = useForm({
    defaultValues: {
      walletId,
      label: "",
      type: "FLASH_WALLET",
      institutionCode: "",
      accountNumber: "",
      isFavorite: false,
    } as BeneficiaryFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(parsed.data);
    },
  });

  const fieldError =
    (name: keyof BeneficiaryFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{tBeneficiary("wallet.beneficiaries.add")}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Subscribe selector={(state) => state.values.type}>
            {(type) =>
              type === "FLASH_WALLET" ? (
                <WalletLookupByPhone
                  onResolved={(resolved) => {
                    form.setFieldValue("accountNumber", resolved.walletNumber);
                    if (!form.getFieldValue("label") && resolved.displayName) {
                      form.setFieldValue("label", resolved.displayName);
                    }
                  }}
                />
              ) : null
            }
          </form.Subscribe>

          <form.Field name="label" validators={{ onChange: fieldError("label") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="label">
                  {tBeneficiary("wallet.beneficiaries.form.label")}
                </Label>
                <Input
                  id="label"
                  value={field.state.value}
                  placeholder={tBeneficiary(
                    "wallet.beneficiaries.form.labelPlaceholder"
                  )}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="type" validators={{ onChange: fieldError("type") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label>{tBeneficiary("wallet.beneficiaries.form.type")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(v as BeneficiaryFormValues["type"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BENEFICIARY_TYPES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field
            name="institutionCode"
            validators={{ onChange: fieldError("institutionCode") }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="institutionCode">
                  {tBeneficiary("wallet.beneficiaries.form.institutionCode")}
                </Label>
                <Input
                  id="institutionCode"
                  value={field.state.value}
                  placeholder={tBeneficiary(
                    "wallet.beneficiaries.form.institutionCodePlaceholder"
                  )}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <form.Field
            name="accountNumber"
            validators={{ onChange: fieldError("accountNumber") }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="accountNumber">
                  {tBeneficiary("wallet.beneficiaries.form.accountNumber")}
                </Label>
                <Input
                  id="accountNumber"
                  value={field.state.value}
                  placeholder={tBeneficiary(
                    "wallet.beneficiaries.form.accountNumberPlaceholder"
                  )}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="isFavorite">
            {(field) => (
              <div className="flex items-center gap-2 sm:col-span-2">
                <Checkbox
                  id="isFavorite"
                  checked={Boolean(field.state.value)}
                  onCheckedChange={(checked) =>
                    field.handleChange(checked === true)
                  }
                />
                <Label htmlFor="isFavorite" className="cursor-pointer">
                  {tBeneficiary("wallet.beneficiaries.form.isFavorite")}
                </Label>
              </div>
            )}
          </form.Field>

          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <MutationConfirmPreview
      open={preview !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreview(null);
      }}
      data={preview}
      title={t("common.mutationPreview.createTitle")}
      description={t("common.mutationPreview.createDescription")}
      t={tBeneficiary}
      pending={pending}
      onConfirm={() => {
        if (!preview) return;
        createBeneficiary.mutate(
          {
            walletId: preview.walletId,
            label: preview.label,
            type: preview.type,
            institutionCode: preview.institutionCode || undefined,
            accountNumber: preview.accountNumber,
            isFavorite: preview.isFavorite,
          },
          {
            onSuccess: () => {
              notifSilence(tBeneficiary("wallet.beneficiaries.feedback.created"));
              setPreview(null);
              onOpenChange(false);
            },
            onError: (error) => notifSilenceError(error.message),
          }
        );
      }}
    />
    </>
  );
}
